import { CommonModule } from '@angular/common';
import { AfterContentInit, Component, input, output, contentChildren } from '@angular/core';

import { TabComponent } from './tab/tab.component';

import type { ITabConfig } from '../../interfaces/tab.interface';

/**
 * TabsComponent is a container component that manages multiple TabComponent instances.
 * It handles the logic for activating and displaying the selected tab.
 */
@Component({
  selector: 'app-tabs',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './tabs.component.html',
  styleUrl: './tabs.component.scss',
})
export class TabsComponent implements AfterContentInit {
  readonly tabs = contentChildren(TabComponent);

  /**
   * Index of the initially selected tab. Default is 0.
   */
  public readonly initialTabIndex = input<number>(0);

  /**
   * Configuration object for customizing the tab styles.
   */
  public readonly config = input<ITabConfig | null>(null);

  /**
   * Event emitted when the active tab changes.
   */
  readonly tabChange = output<{ tab: TabComponent; index: number }>();

  private activeTabIndex: number = 0;

  /**
   * Lifecycle hook that is called after the component's content has been fully initialized.
   */
  ngAfterContentInit(): void {
    if (this.tabs()?.length) {
      this.selectTab(this.initialTabIndex() || 0, true);
    }
  }

  /**
   * Activates the tab at the specified index.
   * @param index - The index of the tab to activate.
   * @param isInitial - Flag indicating if this is the initial activation. Default is false.
   */
  public selectTab(index: number, isInitial: boolean = false): void {
    if (isInitial || this.activeTabIndex !== index) {
      this.activeTabIndex = index;

      this.tabs().forEach((tab, i) => {
        tab.isActive = i === index;

        if (tab.isActive) {
          this.tabChange.emit({ tab, index });
        }
      });
    }
  }
}
